import Controller, { DEFAULT_HEADERS, RequestArgs } from './Controller';
import { SessionObject } from '../interfaces/session';
import JSONUtils from '../helpers/json-utils';
import * as APIRoutes from '@mm/api-routes';
import { LocalStorageService } from '@mm/utils';

export const SESSION_KEY_VERSION = '16';
const isSemrush = location.pathname.startsWith('/semrush');

const USE_SESSION_STORAGE = isSemrush;

const SESSION_STORAGE_KEY = location.pathname.startsWith('/semrush')
  ? 'semrushSession'
  : 'session';

const memory: Record<string, string> = {};

const safeSessionStorage = {
  setItem(k, v) {
    try {
      safeSessionStorage.setItem(k, v);
    } catch (err) {
      memory[k] = v;
    }
  },
  getItem(k) {
    try {
      return safeSessionStorage.getItem(k);
    } catch (err) {
      return memory[k];
    }
  },
  removeItem(k) {
    try {
      safeSessionStorage.removeItem(k);
    } catch (err) {
      delete memory[k];
    }
  },
};

export default class AuthenticatedController extends Controller {
  static getSession(): SessionObject | null {
    const session = USE_SESSION_STORAGE
      ? safeSessionStorage.getItem(SESSION_STORAGE_KEY)
      : LocalStorageService.getItem(SESSION_KEY_VERSION);

    if (session === null) {
      return null;
    }

    const parsedSession = JSONUtils.safeParse<SessionObject>(session);

    if (parsedSession) {
      return parsedSession;
    }

    return null;
  }

  static storeSession(session: SessionObject) {
    const serializedSession = JSON.stringify(session);
    if (USE_SESSION_STORAGE) {
      safeSessionStorage.setItem(SESSION_STORAGE_KEY, serializedSession);
    } else {
      LocalStorageService.setItem(SESSION_KEY_VERSION, serializedSession);
    }
  }

  static updateSession(updatePatch: Partial<SessionObject>): void {
    const oldSession = this.getSession();

    if (oldSession) {
      this.storeSession({ ...oldSession, ...updatePatch });
    }
  }

  static async clearSession() {
    const headers =
      AuthenticatedController.addAuthTokenToHeaders(DEFAULT_HEADERS);
    const session = AuthenticatedController.getSession();

    if (!session) return;

    if (USE_SESSION_STORAGE) {
      safeSessionStorage.removeItem(SESSION_STORAGE_KEY);
    } else {
      LocalStorageService.removeItem(SESSION_KEY_VERSION);
    }

    await super.post(APIRoutes.SESSION_LOGOUT, {}, null, headers, null, true);
  }

  static async get(
    route: string,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    signal: AbortSignal | null = null,
    skipCamelize?: boolean,
  ) {
    return Controller.get(
      route,
      args,
      AuthenticatedController.addAuthTokenToHeaders(headers),
      signal,
      skipCamelize,
    );
  }

  static put(
    route: string,
    body?: any,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    skipSnake?: boolean,
    signal: AbortSignal | null = null,
    skipCamelize?: boolean,
  ) {
    return Controller.put(
      route,
      body,
      args,
      AuthenticatedController.addAuthTokenToHeaders(headers),
      skipSnake,
      null,
      skipCamelize,
    );
  }

  static post(
    route: string,
    body?: any,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    signal: AbortSignal | null = null,
    withCredentials: boolean = false,
    skipSnake?: boolean,
  ) {
    return Controller.post(
      route,
      body,
      args,
      AuthenticatedController.addAuthTokenToHeaders(headers),
      signal,
      withCredentials,
      skipSnake,
    );
  }

  static patch(
    route: string,
    body?: any,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
  ) {
    return Controller.patch(
      route,
      body,
      args,
      AuthenticatedController.addAuthTokenToHeaders(headers),
    );
  }

  static delete(
    route: string,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    body?: any,
  ) {
    return Controller.delete(
      route,
      args,
      AuthenticatedController.addAuthTokenToHeaders(headers),
      null,
      body,
    );
  }

  static deleteWithJsonResult(
    route: string,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    body?: any,
  ) {
    return Controller.deleteWithJsonResult(
      route,
      args,
      AuthenticatedController.addAuthTokenToHeaders(headers),
      null,
      body,
    );
  }

  static addAuthTokenToHeaders(headers) {
    let token: string | null = null;

    const session = AuthenticatedController.getSession();
    if (session !== null) {
      token = session.authToken;
    }

    return {
      ...headers,
      Authorization: token,
    };
  }
}
